import { BanknoteArrowDown, Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  InvoiceStatus,
  type StudentSubscriptionInvoice,
  useGetStudentSubscriptionInvoices,
} from '~entities/student/api/get-student-subscription-invoices';
import { useExecuteChargeFromWallet } from '~entities/wallet/api/execute-charge-from-wallet';

import { ApiResponseStatus } from '~shared/api/base-api';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

type WalletExecutionMode = 'auto' | 'manual';

const walletEligibleInvoiceStatuses = [
  InvoiceStatus.FAILED,
  InvoiceStatus.PARTIALLY_PAID,
  InvoiceStatus.PENDING,
  InvoiceStatus.SCHEDULED,
  InvoiceStatus.INVOICE_SCHEDULED,
] satisfies Array<InvoiceStatus>;

const formatInvoiceDate = (value?: string | null) =>
  formatDateOnlySafe(value, 'MMM d, yyyy', 'N/A');

const getInvoiceLabel = (invoice: StudentSubscriptionInvoice) =>
  invoice.productName.trim() || `Invoice ${invoice.invoiceId}`;

const getInvoiceDueTime = (invoice: StudentSubscriptionInvoice) => {
  const date = new Date(invoice.dueDate ?? invoice.invoiceDate);
  return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
};

const sortInvoicesByDueDate = (invoices: Array<StudentSubscriptionInvoice>) =>
  [...invoices].sort((a, b) => getInvoiceDueTime(a) - getInvoiceDueTime(b));

interface WalletExecutionDialogProps {
  parentId: string;
  studentId: string;
  currentWalletBalance: number;
  currencySymbol: string;
}

export const WalletExecutionDialog = ({
  parentId,
  studentId,
  currentWalletBalance,
  currencySymbol,
}: WalletExecutionDialogProps) => {
  const [open, setOpen] = useState(false);
  const [executionMode, setExecutionMode] =
    useState<WalletExecutionMode>('auto');
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Array<string>>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState('');

  const executeCharge = useExecuteChargeFromWallet();
  const {
    data: walletInvoices = [],
    refetch: refetchWalletInvoices,
    isFetching: isFetchingWalletInvoices,
  } = useGetStudentSubscriptionInvoices({
    studentId,
    status: walletEligibleInvoiceStatuses,
  });

  useEffect(() => {
    if (open) {
      setExecutionMode('auto');
      setSelectedInvoiceIds([]);
      setSearchQuery('');
      void refetchWalletInvoices();
    }
  }, [open, refetchWalletInvoices]);

  const sortedInvoices = useMemo(
    () =>
      sortInvoicesByDueDate(
        walletInvoices.filter((invoice) =>
          walletEligibleInvoiceStatuses.includes(invoice.status),
        ),
      ),
    [walletInvoices],
  );

  const filteredInvoices = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return sortedInvoices;

    return sortedInvoices.filter((invoice) => {
      return (
        invoice.invoiceId.toLowerCase().includes(normalizedQuery) ||
        getInvoiceLabel(invoice).toLowerCase().includes(normalizedQuery) ||
        invoice.status.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [searchQuery, sortedInvoices]);

  const selectedInvoiceIdSet = useMemo(
    () => new Set(selectedInvoiceIds),
    [selectedInvoiceIds],
  );

  const previewSourceInvoices = useMemo(() => {
    if (executionMode === 'auto') return sortedInvoices;

    return sortedInvoices.filter((invoice) =>
      selectedInvoiceIdSet.has(invoice.invoiceId),
    );
  }, [executionMode, selectedInvoiceIdSet, sortedInvoices]);

  const walletApplicationPreview = useMemo(() => {
    let remainingBalance = currentWalletBalance;

    const invoices = previewSourceInvoices.map((invoice) => {
      const outstandingAmount = Number(invoice.outstandingAmount) || 0;
      const amountToApply = Math.min(remainingBalance, outstandingAmount);
      remainingBalance = Math.max(0, remainingBalance - amountToApply);

      return {
        invoice,
        amountToApply,
        remainingInvoiceBalance: Math.max(
          0,
          outstandingAmount - amountToApply,
        ),
      };
    });

    return {
      invoices,
      remainingBalance,
      totalApplied: currentWalletBalance - remainingBalance,
    };
  }, [currentWalletBalance, previewSourceInvoices]);

  const toggleSelectedInvoice = (invoiceId: string) => {
    setSelectedInvoiceIds((currentInvoiceIds) =>
      currentInvoiceIds.includes(invoiceId)
        ? currentInvoiceIds.filter(
            (currentInvoiceId) => currentInvoiceId !== invoiceId,
          )
        : [...currentInvoiceIds, invoiceId],
    );
  };

  const submitDisabled =
    executeCharge.isPending ||
    currentWalletBalance <= 0 ||
    (executionMode === 'auto' && sortedInvoices.length === 0) ||
    (executionMode === 'manual' && selectedInvoiceIds.length === 0);

  const handleChargeExecutionFromWallet = async () => {
    try {
      const response = await executeCharge.mutateAsync({
        studentId,
        parentId,
        ...(executionMode === 'manual'
          ? {
              invoiceIds: previewSourceInvoices.map(
                (invoice) => invoice.invoiceId,
              ),
            }
          : {}),
      });

      if (response.status === ApiResponseStatus.SUCCESS) {
        if (response.data.processedInvoices.length > 0) {
          toast.success(
            `Successfully processed ${response.data.processedInvoices.length} invoice(s).`,
          );
          setOpen(false);
        } else {
          toast.warning('No invoices to process.');
        }
      } else {
        toast.warning(response.message);
      }
    } catch (error) {
      console.error('Error executing charge from wallet:', error);
      toast.error('Failed to execute charge from wallet.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='outline'
            size='sm'
            className='h-7'
            disabled={currentWalletBalance <= 0}
            onClick={() => {
              setOpen(true);
            }}
          >
            <BanknoteArrowDown />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Execute Pending Charges from Wallet</p>
        </TooltipContent>
      </Tooltip>
      <DialogContent className='max-h-[85vh] overflow-auto sm:max-w-[760px]'>
        <DialogHeader>
          <DialogTitle>Execute Pending Charges from Wallet</DialogTitle>
          <DialogDescription>
            Review eligible invoices before applying wallet credit.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 py-4'>
          <div className='grid gap-2 rounded-md border p-3'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div>
                <div className='text-sm font-medium'>
                  Current wallet balance
                </div>
                <div className='text-2xl font-semibold'>
                  {currencySymbol}
                  {currentWalletBalance.toFixed(2)}
                </div>
              </div>
              <div className='text-right text-sm text-muted-foreground'>
                <div>
                  Preview applies: {currencySymbol}
                  {walletApplicationPreview.totalApplied.toFixed(2)}
                </div>
                <div>
                  Remaining wallet credit: {currencySymbol}
                  {walletApplicationPreview.remainingBalance.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          <div className='grid gap-2'>
            <Label>Application Mode</Label>
            <RadioGroup
              value={executionMode}
              onValueChange={(value) => {
                setExecutionMode(value as WalletExecutionMode);
              }}
              className='grid gap-2 sm:grid-cols-2'
            >
              <Label
                htmlFor='wallet-auto-apply'
                className='flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/50'
              >
                <RadioGroupItem
                  id='wallet-auto-apply'
                  value='auto'
                  className='mt-1'
                />
                <div>
                  <div className='font-medium'>Auto-apply oldest first</div>
                  <div className='text-sm text-muted-foreground'>
                    Apply credit using the existing due-date order.
                  </div>
                </div>
              </Label>
              <Label
                htmlFor='wallet-manual-apply'
                className='flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/50'
              >
                <RadioGroupItem
                  id='wallet-manual-apply'
                  value='manual'
                  className='mt-1'
                />
                <div>
                  <div className='font-medium'>Select specific invoices</div>
                  <div className='text-sm text-muted-foreground'>
                    Choose which invoices can receive wallet credit.
                  </div>
                </div>
              </Label>
            </RadioGroup>
          </div>

          <div className='grid gap-2'>
            <Label>Eligible Invoices</Label>
            <div className='relative'>
              <Search className='absolute left-2.5 top-2.5 size-4 text-muted-foreground' />
              <Input
                placeholder='Search by invoice, product, or status'
                className='pl-8'
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
            </div>
            <div className='max-h-[250px] overflow-y-auto rounded-md border'>
              {isFetchingWalletInvoices ? (
                <div className='flex items-center justify-center gap-2 p-4 text-muted-foreground'>
                  <Loader2 className='size-4 animate-spin' />
                  Loading invoices
                </div>
              ) : filteredInvoices.length > 0 ? (
                filteredInvoices.map((invoice) => {
                  const checked = selectedInvoiceIdSet.has(invoice.invoiceId);

                  return (
                    <Label
                      key={invoice.invoiceId}
                      htmlFor={`wallet-invoice-${invoice.invoiceId}`}
                      className='flex cursor-pointer gap-3 border-b p-3 last:border-b-0 hover:bg-muted/50'
                    >
                      {executionMode === 'manual' ? (
                        <Checkbox
                          id={`wallet-invoice-${invoice.invoiceId}`}
                          checked={checked}
                          className='mt-1'
                          onCheckedChange={() => {
                            toggleSelectedInvoice(invoice.invoiceId);
                          }}
                        />
                      ) : (
                        <div className='mt-1 flex size-4 items-center justify-center rounded-full border text-[10px] text-muted-foreground'>
                          {sortedInvoices.findIndex(
                            (sortedInvoice) =>
                              sortedInvoice.invoiceId === invoice.invoiceId,
                          ) + 1}
                        </div>
                      )}
                      <div className='grid flex-1 gap-2'>
                        <div className='flex flex-wrap items-center justify-between gap-2'>
                          <div className='font-medium text-primary'>
                            #{invoice.invoiceId}
                          </div>
                          <div className='text-sm capitalize text-muted-foreground'>
                            {invoice.status}
                          </div>
                        </div>
                        <div>{getInvoiceLabel(invoice)}</div>
                        <div className='grid gap-1 text-sm text-muted-foreground sm:grid-cols-3'>
                          <span>
                            Due:{' '}
                            {formatInvoiceDate(
                              invoice.dueDate ?? invoice.invoiceDate,
                            )}
                          </span>
                          <span>
                            Total: {currencySymbol}
                            {Number(invoice.totalAmount).toFixed(2)}
                          </span>
                          <span>
                            Remaining: {currencySymbol}
                            {Number(invoice.outstandingAmount).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </Label>
                  );
                })
              ) : (
                <div className='flex items-center justify-center p-4 text-muted-foreground'>
                  No eligible invoices found
                </div>
              )}
            </div>
          </div>

          <div className='grid gap-2'>
            <Label>Preview</Label>
            <div className='rounded-md border'>
              {walletApplicationPreview.invoices.length > 0 ? (
                walletApplicationPreview.invoices.map(
                  ({ invoice, amountToApply, remainingInvoiceBalance }) => (
                    <div
                      key={invoice.invoiceId}
                      className='flex flex-wrap items-center justify-between gap-2 border-b p-3 text-sm last:border-b-0'
                    >
                      <div>
                        <div className='font-medium'>
                          {getInvoiceLabel(invoice)}
                        </div>
                        <div className='text-muted-foreground'>
                          #{invoice.invoiceId} - Due{' '}
                          {formatInvoiceDate(
                            invoice.dueDate ?? invoice.invoiceDate,
                          )}
                        </div>
                      </div>
                      <div className='text-right'>
                        <div>
                          Apply {currencySymbol}
                          {amountToApply.toFixed(2)}
                        </div>
                        <div className='text-muted-foreground'>
                          Invoice remaining {currencySymbol}
                          {remainingInvoiceBalance.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className='p-3 text-sm text-muted-foreground'>
                  Select invoices to preview wallet application.
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleChargeExecutionFromWallet}
            disabled={submitDisabled}
          >
            {executeCharge.isPending ? 'Processing...' : 'Apply Wallet Credit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
